import {Component, OnInit, OnDestroy} from '@angular/core';
import {BusinessEntityService} from '../../../../services/businessEntity.service';
import BusinessEntity from '../../../../services/businessEntity.model';
import {FarmSiteService} from '../../../../services/farmSite.service';
import FarmSiteModel from '../../../../services/farmSite.model';
import {ItemMasterService} from '../../../../services/itemMaster.service';
import ItemMasterModel from '../../../../services/itemMaster.model';
import {TransactionTypeService} from '../../../../services/transactionType.service';
import TransactionTypeModel from '../../../../services/transactionType.model';
import {TransactionsService} from '../../../../services/transactions.service';
import TransactionsModel from '../../../../services/transactions.model';
import {UomService} from '../../../../services/uom.service';
import UOMMasterModel from '../../../../services/uom.model';
import {CustomerPondService} from '../../../../services/customerPond.service';
import CustomerPondModel from '../../../../services/customerPond.model';
import {VendorsService} from '../../../../services/vendors.service';
import Vendors from '../../../../services/vendor.model';
import {PaymentTransactionService} from '../../../../services/paymentTransaction.service';
import PaymentTransactionModel from '../../../../services/paymentTransaction.model';
import {Sidenavaction} from '../../../../../assets/js/application/sidenavaction';
import * as _ from 'lodash';
import * as moment from 'moment';
import {ActivatedRoute} from '@angular/router';
import AttachmentsService from 'src/app/services/attachements.service';
import AttachmentsModel from 'src/app/services/attachments.model';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * @component MaterialTransactionComponent
 * @selector app-material-transaction-uc
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `MaterialTransactionComponent` manages the recording and tracking of material transactions within the farm management system.
 * It handles various types of transactions such as purchases, issues, transfers, and returns for inventory and expense items.
 *
 * ## Key Responsibilities:
 * 1. **Transaction Recording**: Allows users to record new material transactions with details like item, quantity, unit cost, and vendor.
 * 2. **Inventory Management**: Tracks inventory levels and updates stock based on transaction types (e.g., Purchase adds stock, Issue reduces stock).
 * 3. **Payment Integration**: Seamlessly integrates with the payment system to record payments for purchase transactions.
 * 4. **Data Retrieval**: Fetches necessary data such as business entities, farm sites, items, vendors, and transaction types.
 * 5. **Form Validation**: Ensures data integrity through reactive form validation for required fields and data formats.
 * 6. **File Attachment**: Supports uploading attachments (e.g., invoices, receipts) associated with transactions.
 * 7. **Role-Based Access**: Adapts the interface and available options based on the user's role (e.g., Farmer, Farm Manager, Business Admin).
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Can manage transactions for their entire farm business.
 *   - **Farm Manager**: Can manage transactions for assigned farm sites.
 * - **Provider Category Users**:
 *   - **Business Admin**: Can manage transactions for provider businesses.
 * - **Other Categories**:
 *   - **Employee**: Can record transactions for selected businesses.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context ('Farm', 'Provider').
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for display and context.
 * - `LoginUserName`: Used for tracking creator/updater.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` determines user context and loads initial data (businesses, farm sites, vendors).
 * 2. **Selection**: User selects Business, Farm Site, and Item.
 * 3. **Transaction Type**: Based on the selected item (Inventory vs. Expense), available transaction types are filtered.
 * 4. **Input**: User enters transaction details (quantity, cost, date, etc.).
 * 5. **Submission**: `inventoryTransaction` validates and submits the transaction.
 * 6. **Payment**: If applicable (Purchase), `getPaymentTransaction` initiates the payment recording process.
 *
 * ## UI State Management:
 * - **Loading**: `pgLoad` manages page loading state.
 * - **Form**: `registerForm` handles input validation and state.
 * - **Tabs**: `tabIndex` manages switching between Inventory and Expense tabs.
 */
@Component({
  selector: 'app-material-transaction-uc',
  templateUrl: './material-transaction.component.html',
  styleUrls: ['./material-transaction.component.scss']
})
export class MaterialTransactionComponent implements OnInit, OnDestroy {
  /** @type {Array<BusinessEntity>} List of available business entities for selection. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {any[]} Filtered list of options for the business autocomplete dropdown. */
  public filteredOptions: any[] = [];

  /** @type {string} Search string for business selection. */
  businessSearch: string;

  /** @type {BusinessEntity} The currently selected business entity. */
  public businessEntity: BusinessEntity;

  /** @type {Array<FarmSiteModel>} List of farm sites associated with the selected business. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<ItemMasterModel>} List of item masters (products) available for transaction. */
  public itemMasters: Array<ItemMasterModel>;

  /** @type {ItemMasterModel} The currently selected item master model. */
  public itemMaster: ItemMasterModel;

  /** @type {Array<TransactionTypeModel>} List of available transaction types. */
  public transactionTypes: Array<TransactionTypeModel>;

  /** @type {Array<TransactionsModel>} List of transactions (unused in current logic). */
  public transactions: Array<TransactionsModel>;

  /** @type {TransactionsModel} Model used for binding transaction data. */
  public transaction: TransactionsModel;

  /** @type {UomService} Service for UOM operations (injected but public property). */
  public uomService: UomService;

  /** @type {Array<UOMMasterModel>} List of available UOMs. */
  public uoms: Array<UOMMasterModel>;

  /** @type {UOMMasterModel} Selected UOM model. */
  public uom: UOMMasterModel;

  /** @type {Array<CustomerPondModel>} List of customer ponds (unused in current logic). */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {Array<Vendors>} List of vendors available for selection. */
  public vends: Array<Vendors>;

  /** @type {PaymentTransactionModel} Model for recording payment details associated with the transaction. */
  public paymentTransaction: PaymentTransactionModel;

  /** @type {AttachmentsModel} Model for file attachments. */
  public mtlAttachmentsModel: AttachmentsModel;

  /** @type {boolean} Flag indicating if item master data is recent. */
  public isRecentitemMasterData = false;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} Name of the logged-in user. */
  public LoginUserName: string;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {File | any} Selected image file for upload. */
  public imageFile: File | any;

  /** @type {string | ArrayBuffer} Base64 string of the selected file. */
  public selectedbase64file: string | ArrayBuffer;

  /** @type {boolean} Flag indicating if the form has been cleared. */
  public isCleared: boolean;

  /** @type {Array<any>} List of UOM data for dropdown. */
  public uomData = new Array();

  /** @type {Array<any>} List of attachments. */
  public attachments = new Array();

  /** @type {boolean} Flag indicating if the form has been submitted successfully. */
  public issubmitted: boolean;

  /** @type {string} Name of the selected transaction type. */
  public transactionName: string;

  /** @type {Array<any>} Original list of transaction types. */
  public originaltransactiontypeData = new Array();

  /** @type {boolean} Flag indicating if the selected item is an inventory item. */
  public findinginventorytruefalse: boolean;

  /** @type {boolean} Flag indicating if the form has been submitted (validation check). */
  public submitted = false;

  /** @type {any} Object containing on-hand quantity details. */
  public transactionOnHand: any;

  /** @type {Array<any>} Array of business items associated with the selected item. */
  public businessitmsArr = new Array();

  /** @type {number} Flag for UOM conversion status (unused). */
  public isconversionUmoornot: number;

  /** @type {Array<any>} Dropdown list for pond details. */
  public pondsDetails_dropdown = new Array();

  /** @type {boolean} Flag indicating if the selected item is non-stockable. */
  public selectedItemisNonstockable = false;

  /** @type {boolean} Flag indicating if the item is stockable. Default is `true`. */
  public stockable = true;

  /** @type {any} Data of the latest created transaction. */
  public latestTransactionData: any;

  // /** multi select for devices **/
  /** @type {Array<any>} List of items for pond multi-select dropdown. */
  public ponddropdownList = [];

  /** @type {Array<any>} Selected items in pond multi-select dropdown. */
  public pondselectedItems = [];

  /** @type {object} Settings for pond multi-select dropdown. */
  public ponddropdownSettings = {};

  /** @type {boolean} Flag to disable download button (unused). */
  public disableDownload: boolean;

  /** @type {any} Transaction date. */
  public transactionDate: any;

  /** @type {any} Payment details object. */
  public paymentDetails: any;

  /** @type {string} Keyword for item search. */
  public keyword: string;

  /** @type {any} Generic item holder. */
  public item: any;

  /** @type {string} Keyword for business search. */
  public businesskeyword: string;

  /** @type {any} Business item holder. */
  public businessItem: any;

  /** @type {number} Current tab index (0 for Inventory, 1 for Expense). */
  public tabIndex: number = 0;

  /** @type {Array<any>} Combined list of inventory and expense items. */
  public inventoryExpenseItems = new Array();

  /** @type {Array<any>} List of inventory items. */
  public inventoryItems = new Array();

  /** @type {Array<any>} List of expense items. */
  public expenseItems = new Array();

  /** @type {any} Filtered item options for autocomplete. */
  filteredItemOptions: any;

  /** @type {any} Search string for item. */
  itemSearch: any;

  /** @type {any} Application permissions object. */
  public appPermission: any;

  /** @type {boolean} Flag indicating if a file is selected. */
  public isFileSelected: boolean;

  /** @type {string} Name of the selected file. */
  public fileName: string;

  /** multi select for devices **/
  /** @type {FormGroup} The main reactive form for transaction details. */
  public registerForm: FormGroup;

  /** @type {boolean} Flag controlling vendor dropdown visibility. */
  public vendorsDropdownOpen: boolean = false;

  /** @type {string} Search string for vendor name. */
  public vendorNameSearch: string;

  /** @type {any[]} Filtered vendor names for autocomplete. */
  public filteredVendorNames: any[] = [];

  /** @type {boolean} Flag to disable submit button during processing. */
  public submitButtonDisabled: boolean;

  /** @type {FormControl} Control for transaction date with validation. */
  transactionDateControl = new FormControl(new Date(), Validators.required);

  /** @type {Date} Maximum allowed date for transaction. */
  maxDate: Date = new Date();

  /**
   * Constructor for MaterialTransactionComponent.
   * Initializes services and the reactive form.
   *
   * @param {ToastrService} toasterService - Service for toast notifications.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {AttachmentsService} mtlAttachmentsService - Service for handling attachments.
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @param {PaymentTransactionService} paymentTransactionservice - Service for payment transactions.
   * @param {VendorsService} vendorsService - Service for vendor operations.
   * @param {CustomerPondService} customerPondsService - Service for customer pond operations.
   * @param {ItemMasterService} itemMasterService - Service for item master operations.
   * @param {TransactionTypeService} transactionTypeService - Service for transaction type operations.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {TransactionsService} transactionsService - Service for transaction operations.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   *
   * @service ToastrService
   * @service FormBuilder
   * @service AttachmentsService
   * @service ActivatedRoute
   * @service PaymentTransactionService
   * @service VendorsService
   * @service CustomerPondService
   * @service ItemMasterService
   * @service TransactionTypeService
   * @service BusinessEntityService
   * @service FarmSiteService
   * @service TransactionsService
   * @service Sidenavaction
   * @service CommonMethodsService
   * @service LocationStrategy
   * @service PreviousRouteService
   */
  constructor(private toasterService: ToastrService, public formBuilder: FormBuilder, public mtlAttachmentsService: AttachmentsService, public route: ActivatedRoute, public paymentTransactionservice: PaymentTransactionService, public vendorsService: VendorsService, public customerPondsService: CustomerPondService, public itemMasterService: ItemMasterService, public transactionTypeService: TransactionTypeService, public businessEntityService: BusinessEntityService, public farmSiteService: FarmSiteService, public transactionsService: TransactionsService, public sidenavaction: Sidenavaction,
              public commonMethodsService: CommonMethodsService, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.paymentTransaction = new PaymentTransactionModel();
    this.transaction = new TransactionsModel();
    this.mtlAttachmentsModel = new AttachmentsModel();
    // Initialize the register form with validation
    this.registerForm = this.formBuilder.group({
      businessnameField: ['', [Validators.required]],
      locationameField: ['', [Validators.required]],
      itemnameField: ['', [Validators.required]],
      transactionTypeField: ['', [Validators.required]],
      tankid: [null],
      transactionDateField: this.transactionDateControl,
      stage: ['', Validators.required],
      transactionQuantityField: ['', [Validators.required]],
      transactionUomField: ['', [Validators.required]],
      unitCostfield: [null],
      unitValueField: [null],
      gstfield: [null],
      vendorSelection: [null],
      amountpaid: [null],
      invoiceVoucher: [null],
      description: ['', [Validators.maxLength(200)]],
      fileAttachment: [null]
    });
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Initializes date picker and default values.
   * - Sets up the previous route.
   * - Determines logic flow based on `loginBusinessCategory`:
   *   - **Farm**: Auto-sets business ID and fetches farm sites/vendors based on `loginPersonType`.
   *   - **Provider**: Fetches provider-specific data.
   *   - **Default**: Fetches all business entities for selection.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  public async ngOnInit() {
    this.appPermission = this.route.snapshot.data['rolepermission'];
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.keyword = 'item_name';
      this.loginPersonType = localStorage.getItem('loginPersonType');
      /* user login details */
      this.businesskeyword = 'businessName';
      this.LoginUserName = localStorage.getItem('LoginUserName');
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      /* user login details end */
      this.initializedatePicker();
      this.isFileSelected = false;
      this.imageFile = undefined;
      this.selectedbase64file = undefined;
      this.findinginventorytruefalse = true;
      this.isCleared = false;
      this.transactionTypes = [];
      this.farmSites = [];
      this.disableDownload = true;
      this.inventoryExpenseItems = [];
      this.attachments = [];
      this.transaction.stage = 'Growout';
      this.uomData = [];
      this.vendorNameSearch = undefined;
      $('#imageUpload').val('');
      this.transaction.transaction_date = (moment(new Date()).format('DD-MMM-YYYY')).toString(); // "2020-05-20"
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.transaction.business_id = localStorage.businessID;
        this.registerForm.get('businessnameField').patchValue(this.loginUserBusinessName);
        this.transaction.business_name = this.loginUserBusinessName;
        this.transaction.farm_site_id = undefined;
        let promiseResults = new Array();
        await Promise.all([
          await this.farmSiteService.getFarmeSitesByFarmsId(this.transaction.business_id),
          await this.vendorsService.getVendorsByBusinessId(this.transaction.business_id),
        ]).then((resolve) => {
          promiseResults = resolve;
        }).catch((reject) => {
          this.toasterService.error(reject);
        });
        this.farmSites = promiseResults[0];
        this.vends = promiseResults[1];
        this.setSingleValueForFormLocation();
        await this.getitemdatafarmsitedta();
      }
      if (this.loginPersonType == 'Farm Manager' && this.loginUserBusinessCategory == 'Farm') {
        this.transaction.business_id = localStorage.businessID;
        this.registerForm.get('businessnameField').patchValue(this.loginUserBusinessName);
        this.transaction.business_name = this.loginUserBusinessName;
        this.transaction.farm_site_id = undefined;
        let promiseResults = new Array();
        await Promise.all([
          await this.farmSiteService.getFarmeSitesByFarmManagerIdInFarmSpend(this.transaction.business_id, localStorage.personID),
          await this.vendorsService.getVendorsByBusinessId(this.transaction.business_id),
        ]).then((resolve) => {
          promiseResults = resolve;
        }).catch((reject) => {
          this.toasterService.error(reject);
        });
        this.farmSites = promiseResults[0];
        this.vends = promiseResults[1];
        this.setSingleValueForFormLocation();
        await this.getitemdatafarmsitedta();
      }
      if (this.loginPersonType != 'Business Admin' && this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager') {
        let farmBusiness = await this.businessEntityService.getAllBusiness();
        this.businessEntities = farmBusiness.filter(businessEntity => businessEntity.businessType && businessEntity.businessType.length > 0 && businessEntity.businessType[0].businessTypeID && businessEntity.businessType[0].businessTypeID === '618916f2b4391c6d48137e3d');
        this.filteredOptions = this.businessEntities;
        if (this.transaction.business_id) {
          this.vends = await this.vendorsService.getVendorsByBusinessId(this.transaction.business_id);
        }
      }
      if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        let promiseResults = new Array();
        await Promise.all([
          await this.businessEntityService.getAllfarmerByProviderID(localStorage.businessID),
          await await this.vendorsService.getVendorsByBusinessId(this.transaction.business_id),
        ]).then((resolve) => {
          promiseResults = resolve;
        }).catch((reject) => {
          this.toasterService.error(reject);
        });
        this.businessEntities = promiseResults[0];
        this.vends = promiseResults[1];
        this.transaction.business_name = this.loginUserBusinessName;
      }
      this.issubmitted = false;
      this.isconversionUmoornot = 0;
      this.submitButtonDisabled = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the HTML template.
   * @returns {any} The controls of the `registerForm`.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests.
   */
  public ngOnDestroy(): void {
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.vendorsService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.itemMasterService.cancelOnGoingHttpRequests();
    this.customerPondsService.cancelOnGoingHttpRequests();
    this.transactionTypeService.cancelOnGoingHttpRequests();
    this.transactionsService.cancelOnGoingHttpRequests();
    this.paymentTransactionservice.cancelOnGoingHttpRequests();
  }

  /**
   * @method filterVendors
   * @description Filters the `vends` list based on the user's input in the vendor selection field.
   * Updates `filteredVendorNames` with the result.
   * @side-effect Updates `filteredVendorNames`.
   */
  public filterVendors(): void {
    const value = (this.registerForm.get('vendorSelection')!.value || '') as Vendors | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();
    this.filteredVendorNames = this.vends.filter(o =>
      o.name.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method selectVendorOption
   * @description Handles the selection of a vendor from the dropdown.
   * Updates the form value and the `paymentTransaction.vendor_id`.
   * @param {any} vendor - The selected vendor object.
   * @side-effect Updates `registerForm`, `vendorsDropdownOpen`, `vendorNameSearch`, `paymentTransaction.vendor_id`.
   */
  public selectVendorOption(vendor: any): void {
    this.registerForm.get('vendorSelection')!.setValue(vendor.name);
    $('.custom-select label').css({'top': 0});
    this.vendorsDropdownOpen = false;
    this.vendorNameSearch = vendor.name;
    this.paymentTransaction.vendor_id = vendor.id;
  }

  /**
   * @method getitemdatafarmsitedta
   * @async
   * @description Fetches item master data and farm sites based on the selected business.
   * Handles logic for different business types ('Farm' vs 'Farm Provider').
   * @side-effect Updates `farmSites`, `transaction.farm_site_id`, calls `setSingleValueForFormLocation`.
   */
  public async getitemdatafarmsitedta() {
    try {
      this.isRecentitemMasterData = false;
      this.issubmitted = false;
      this.transactionTypes = []; // by click on farm transaction type data is empty
      this.findinginventorytruefalse = true;
      await this.getAllItemMasterbyFarmer();
      this.uomData = [];
      let bussinesstype = '';
      for (let i = 0; i < (this.businessEntities && this.businessEntities.length); i++) {
        let eachObj = this.businessEntities[i];
        if (eachObj.id == this.transaction.business_id) { // this.bussinessId // modal selectbox
          bussinesstype = eachObj.businessType[0].name;
        }
      }
      if (bussinesstype == 'Farm') {
        this.transaction.farm_site_id = undefined;
        this.farmSites = await this.farmSiteService.getFarmeSitesByFarmId(this.transaction.business_id);
        this.setSingleValueForFormLocation();
      } else if (bussinesstype == 'Farm Provider') {
        this.farmSites = [];
        this.transaction.farm_site_id = undefined;
        this.farmSites = await this.farmSiteService.getFarmeSitesBusinessDetailsByProviderId(this.transaction.business_id);
        this.setSingleValueForFormLocation();
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForFormLocation
   * @description Auto-selects the farm site if only one exists.
   * Also sets the `transaction.farm_location` string for display.
   */
  public setSingleValueForFormLocation() {
    if (this.farmSites && this.farmSites.length == 1) {
      this.transaction.farm_site_id = this.farmSites[0].id; // model-binding key
    }
    let farmlocationarr = _.filter(this.farmSites, (o) => {
      return o.id == this.transaction.farm_site_id;
    });
    if (farmlocationarr.length > 0) {
      let farmName = farmlocationarr[0] && farmlocationarr[0].farmNameAtLocation;
      let village = farmlocationarr[0] && farmlocationarr[0].address && farmlocationarr[0].address.village;
      this.transaction.farm_location = (farmName && village) ? (farmName + ',' + village) : ((farmName && !village) ? farmName : (!farmName && village) ? village : '');
    }
    this.getAllpondsByFarmsiteId();
  }

  /**
   * @method filterItems
   * @description Filters the `inventoryExpenseItems` list based on the user's input in the item name field.
   * Updates `filteredItemOptions` with the result.
   * @side-effect Updates `filteredItemOptions`.
   */
  public filterItems(): void {
    const value = (this.registerForm.get('itemnameField')!.value || '') as ItemMasterModel | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.item_name.toLowerCase();
    this.filteredItemOptions = this.inventoryExpenseItems.filter(o =>
      o.item_name.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method selectItemOption
   * @async
   * @description Handles the selection of an item from the dropdown.
   * Updates the form value, `transaction.item_id`, and triggers subsequent data fetches.
   * @param {ItemMasterModel} item - The selected item master object.
   */
  public async selectItemOption(item: ItemMasterModel) {
    this.registerForm.get('itemnameField')!.setValue(item.item_name);
    // this.isDropdownOpen = false;
    this.itemSearch = item.item_name;
    this.transaction.item_id = item.id;
    if (this.transaction.item_id) {
      this.transactionTypes = [];
      this.transaction.transaction_type_id = undefined;
      this.getbusinessitemid();
      this.stockableChecking();
    }
  }

  /**
   * @method getAllpondsByFarmsiteId
   * @async
   * @description Fetches ponds associated with the selected farm site ID.
   * Populates `pondsDetails_dropdown` and `ponddropdownList` for multi-select.
   * @side-effect Updates `customerPonds`, `pondsDetails_dropdown`, `ponddropdownList`.
   */
  public async getAllpondsByFarmsiteId() {
    if (this.transaction.farm_site_id) {
      this.pondsDetails_dropdown = [];
      try {
        this.customerPonds = await this.customerPondsService.getPondsByFarmSiteId([this.transaction.farm_site_id]);
        this.pondsDetails_dropdown = [];
        if (this.customerPonds && this.customerPonds.length > 0) {
          for (let i = 0; i < this.customerPonds.length; i++) {
            if ((this.customerPonds[i].PondCultureData && this.customerPonds[i].PondCultureData.length > 0)) {
              let eachpondculture = this.customerPonds[i].PondCultureData[this.customerPonds[i].PondCultureData.length - 1];
              eachpondculture.pond_name = this.customerPonds[i].name;
              eachpondculture.pond_id = this.customerPonds[i].id;
              eachpondculture.isPreparatory = this.customerPonds[i].isPreparatory;
              eachpondculture.pond_identifier = this.customerPonds[i].pond_identifier;
              this.pondsDetails_dropdown.push(eachpondculture);
            } else if (this.customerPonds[i].isPreparatory == true && this.customerPonds[i].PondCultureData.length == 0) {
              let isPreObj = {
                id: null,
                pond_id: this.customerPonds[i].id,
                pond_name: this.customerPonds[i].name,
                culture_identifier: null,
                isPreparatory: this.customerPonds[i].isPreparatory,
                pond_identifier: this.customerPonds[i].pond_identifier
              };
              this.pondsDetails_dropdown.push(isPreObj);
            }
          }
        }
        /*prepare paond array objects*/
        this.ponddropdownList = [];
        for (let k = 0; k < this.pondsDetails_dropdown.length; k++) {
          let newobj = {
            id: this.pondsDetails_dropdown[k].id,
            itemName: this.pondsDetails_dropdown[k].pond_name,
            pondID: this.pondsDetails_dropdown[k].pond_id,
            cultureIdentifier: this.pondsDetails_dropdown[k].culture_identifier,
            pondIdentifier: this.pondsDetails_dropdown[k].pond_identifier,
            isPreparatory: this.pondsDetails_dropdown[k].isPreparatory
          };
          this.ponddropdownList.push(newobj);
        }
        /*prepare paond array objects end*/
      } catch (error) {
        this.commonMethodsService.userFriendlyAlertMessages(error);
      }
    }
  }

  /**
   * @method getAllItemMasterbyFarmer
   * @async
   * @description Fetches all item masters (products) for the selected business.
   * Categorizes items into `inventoryItems` and `expenseItems` based on `stockable` flag.
   * @side-effect Updates `itemMasters`, `inventoryItems`, `expenseItems`, `inventoryExpenseItems`.
   */
  public async getAllItemMasterbyFarmer() {
    this.inventoryItems = [];
    this.expenseItems = [];
    this.inventoryExpenseItems = [];
    if (this.transaction.business_id) {
      try {
        this.itemMasters = await this.itemMasterService.getAllProductsByBusinessId(this.transaction.business_id);
        for (let k = 0; k < this.itemMasters.length; k++) {
          let eachItem = this.itemMasters[k];
          this.itemMasters[k].item_name = eachItem.item_name + '' + (eachItem && eachItem.sub_type ? '-' + eachItem && eachItem.sub_type : '') + '' + ((eachItem && eachItem.UOMConversion.length > 0 && (eachItem && eachItem.UOMConversion[0] && eachItem.UOMConversion[0].to_value)) ? '-' + (eachItem && eachItem.UOMConversion.length > 0 && (eachItem && eachItem.UOMConversion[0] && eachItem.UOMConversion[0].to_value)) : '') + ((eachItem && eachItem.UOMConversion.length > 0 && (eachItem && eachItem.UOMConversion[0] && eachItem.UOMConversion[0].ToUOM && eachItem.UOMConversion[0].ToUOM.name)) ? (eachItem && eachItem.UOMConversion.length > 0 && (eachItem && eachItem.UOMConversion[0] && eachItem.UOMConversion[0].ToUOM && eachItem.UOMConversion[0].ToUOM.name)) : '');
        }
        this.inventoryExpenseItems = [];
        this.inventoryItems = [];
        this.expenseItems = [];
        for (let i = 0; i < this.itemMasters.length; i++) {
          if (this.itemMasters[i].stockable == true) {
            this.inventoryItems.push(this.itemMasters[i]);
          } else {
            this.expenseItems.push(this.itemMasters[i]);
          }
        }
        if (this.tabIndex == 0) {
          this.inventoryExpenseItems = this.inventoryItems;
        } else {
          this.inventoryExpenseItems = this.expenseItems;
        }
        this.selectedItemisNonstockable = true;
        this.isRecentitemMasterData = true;
        this.transaction.item_id = '';
      } catch (error) {
        this.commonMethodsService.userFriendlyAlertMessages(error);
      }
    }
  }

  /**
   * @method inventoryTab
   * @description Switches the view to the Inventory tab.
   * Resets form fields and loads inventory items.
   */
  public inventoryTab() {
    this.tabIndex = 0;
    this.isFileSelected = false;
    this.imageFile = undefined;
    this.selectedbase64file = undefined;
    this.transaction.item_id = '';
    this.registerForm.get('itemnameField').patchValue('');
    this.isCleared = false;
    this.fileName = undefined;
    this.inventoryExpenseItems = [];
    this.item = undefined;
    this.transactionTypes = [];
    this.transaction.transaction_type_id = undefined;
    this.transactionOnHand = undefined;
    this.pondselectedItems = undefined;
    this.stockable = false;
    this.latestTransactionData = undefined;
    $('#imageUpload').val('');
    if (this.transaction.business_id) {
      this.getAllItemMasterbyFarmer();
      this.transactionTypes = [];
      this.transaction.transaction_type_id = undefined;
      if (this.transactionOnHand) {
        this.transactionOnHand.onHandQuantities = [];
      }
      this.getbusinessitemid();
      this.stockableChecking();
    }
  }

  /**
   * @method expenseTab
   * @description Switches the view to the Expense tab.
   * Resets form fields and loads expense items.
   */
  public expenseTab() {
    this.tabIndex = 1;
    this.isFileSelected = false;
    this.imageFile = undefined;
    this.transaction.item_id = '';
    this.registerForm.get('itemnameField').patchValue('');
    this.isCleared = false;
    this.selectedbase64file = undefined;
    this.fileName = undefined;
    this.inventoryExpenseItems = [];
    this.item = undefined;
    this.transactionTypes = [];
    this.transaction.transaction_type_id = undefined;
    this.transactionOnHand = undefined;
    this.pondselectedItems = undefined;
    this.latestTransactionData = undefined;
    $('#imageUpload').val('');
    if (this.transaction.business_id) {
      this.getAllItemMasterbyFarmer();
      this.transactionTypes = [];
      this.transaction.transaction_type_id = undefined;
      if (this.transactionOnHand) {
        this.transactionOnHand.onHandQuantities = [];
      }
      this.getbusinessitemid();
      this.stockableChecking();
    }
  }


  /**
   * @method stockableChecking
   * @description Checks if the selected item is stockable.
   * Updates the `stockable` flag which controls the visibility of the pond dropdown.
   */
  public stockableChecking() {
    this.stockable = true;
    for (let i = 0; i < this.inventoryExpenseItems.length; i++) {
      if (this.transaction.item_id == this.inventoryExpenseItems[i].id) {
        this.stockable = this.inventoryExpenseItems[i].stockable;
      }
    }
  }

  /**
   * @method getAllTransactionTypes
   * @async
   * @description Fetches all available transaction types.
   * Filters out 'Feed Produced(+)' type.
   */
  public async getAllTransactionTypes() {
    try {
      this.transactionTypes = await this.transactionTypeService.getAllTransactionTypes();
      this.transactionTypes = this.transactionTypes.filter(type => type.transaction_type != 'Feed Produced(+)');
      this.originaltransactiontypeData = _.clone(this.transactionTypes);
      if (this.transaction.transaction_type_id == 4) {
        this.vends = await this.vendorsService.getVendorsByBusinessIdItemId(this.transaction.business_id, this.transaction.item_id);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getVendors
   * @async
   * @description Fetches vendors based on the transaction type and business ID.
   * If transaction type is 4 (Return), fetches vendors associated with the specific item.
   */
  public async getVendors() {
    try {
      if (this.transaction.transaction_type_id == 4) {
        this.vends = await this.vendorsService.getVendorsByBusinessIdItemId(this.transaction.business_id, this.transaction.item_id);
      } else {
        this.vends = await this.vendorsService.getVendorsByBusinessId(this.transaction.business_id);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getBussinessNamebyBussinessId
   * @description Finds and sets the business name based on the selected business ID.
   * @side-effect Updates `transaction.business_name`.
   */
  public getBussinessNamebyBussinessId() {
    let providerarr = _.filter(this.businessEntities, (o) => {
      return o.id == this.transaction.business_id;
    });
    if (providerarr.length > 0) {
      this.transaction.business_name = providerarr[0].businessName;
    }
  }

  /**
   * @method getLocationnamebyLocationId
   * @description Finds and sets the farm location string based on the selected farm site ID.
   * @side-effect Updates `transaction.farm_location`.
   */
  public getLocationnamebyLocationId() {
    this.issubmitted = false;
    let farmlocationarr = _.filter(this.farmSites, (o) => {
      return o.id == this.transaction.farm_site_id;
    });
    if (farmlocationarr.length > 0) {
      let farmName = farmlocationarr[0] && farmlocationarr[0].farmNameAtLocation;
      let village = farmlocationarr[0] && farmlocationarr[0].address && farmlocationarr[0].address.village;
      this.transaction.farm_location = (farmName && village) ? (farmName + ',' + village) : ((farmName && !village) ? farmName : (!farmName && village) ? village : '');
    }
  }

  /**
   * @method getbusinessitemid
   * @async
   * @description Fetches business item details and prepares UOM data.
   * Filters available transaction types based on whether the item is inventory or expense.
   */
  public async getbusinessitemid() {
    await this.getAllTransactionTypes();
    this.transactionTypes = []; // [{"id":1,"name"}]
    let item_id = this.transaction.item_id;
    this.transaction.uom_id = undefined;
    this.businessitmsArr = _.filter(this.inventoryExpenseItems, function (o) {
      return o.id == parseInt(item_id);
    });
    if (this.businessitmsArr.length > 0) {
      this.transaction.business_item_id = this.businessitmsArr[0].BusinessItem[0].id;
      await this.prepareUOMData(this.businessitmsArr[0]);
    }
    // if inventory is false then transaction type is issue otherwise dispaly  all transactiontype in dropdownlist for this finding inventrory true or false
    if (this.businessitmsArr.length > 0) {
      this.findinginventorytruefalse = this.businessitmsArr[0].isInventory;
      if (this.findinginventorytruefalse == false) {
        let data = _.filter(this.originaltransactiontypeData, function (o) {
          return (o.transaction_type == 'Issue(-)') || (o.transaction_type == 'Reversal(-)');
        });
        this.transactionTypes = data;
      } else if (this.findinginventorytruefalse == true) {
        let data = _.filter(this.originaltransactiontypeData, function (o) {
          return (o.transaction_type == 'Transfer(-)/Return(+)') || (o.transaction_type == 'Miscellaneous(-)') || (o.transaction_type == 'Issue(-)') || (o.transaction_type == 'Balance migration(+)') || (o.transaction_type == 'Purchase(+)');
        });
        this.transactionTypes = data;
      }
    }
  }

  /**
   * @method prepareUOMData
   * @async
   * @description Prepares the UOM dropdown data based on the selected item's conversions.
   * Adds 'Gram' option if applicable. Auto-selects UOM if only one option exists.
   */
  public async prepareUOMData(itmsArr: ItemMasterModel) {
    this.uomData = []; // each time i prepare new items in onchane
    if (itmsArr.UOMConversion.length > 0) {
      let uomdataArr = itmsArr.UOMConversion;
      for (let i = 0; i < uomdataArr.length; i++) {
        let eachobj = uomdataArr[i];
        if (itmsArr.item_type_id == 1 && eachobj.from_units_uom_id != 3 && eachobj.to_units_uom_id != 3) {
          let gramobj = {id: 3, name: 'Gram', uomadded: true};
          if (this.transaction.transaction_type_id != 1) {
            this.uomData.push(gramobj);
          }
        }
        let tempfromobj = {'id': eachobj.from_units_uom_id, 'name': eachobj.FromUOM.name};
        let temptoobj = {'id': eachobj.to_units_uom_id, 'name': eachobj.ToUOM.name};
        this.uomData.push(tempfromobj);
        this.uomData.push(temptoobj);
      }
      /*if selected items has isinventory is false then automatically display uom master of name in select uom dropdown*/
      if (this.uomData && this.uomData.length == 1) {
        this.transaction.uom_id = this.uomData[0].id; // here id meansmodel-binding key
      }
    } else {
      this.uomData.push({'id': itmsArr.UOMMaster.id, 'name': itmsArr.UOMMaster.name});
      /*if selected items has isinventory is false then automatically display uom master of name in select uom dropdown*/
      if (this.uomData && this.uomData.length == 1) {
        this.transaction.uom_id = this.uomData[0].id; // here id meansmodel-binding key
      }
    }
  }

  public unitCostError: string = '';

  /**
   * @method inventoryTransaction
   * @async
   * @description Validates and submits the inventory transaction.
   * Handles form validation, business logic checks, file upload, and API calls.
   * If applicable, triggers payment transaction recording.
   */
  public async inventoryTransaction() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    this.submitButtonDisabled = true;
    try {
      this.issubmitted = false;
      if (this.inventoryExpenseItems.length == 0) {
        this.toasterService.clear();
        this.toasterService.warning('No item available for this business ');
        return false;
      }
      this.transaction.transaction_date = moment(this.transactionDateControl.value).format('YYYY-MM-DD')
      if ((this.transaction.unit_cost == undefined) &&
        (((this.findinginventorytruefalse == true) &&
            (this.transactionName == 'Balance migration(+)' || this.transactionName == 'Purchase(+)')) ||
          ((this.findinginventorytruefalse == false) &&
            (this.transactionName == 'Issue(-)' || this.transactionName == 'Reversal(-)'))) &&
        this.transaction.transaction_type_id != undefined) {
        this.toasterService.clear();
        this.toasterService.error('Please enter unit cost');
        this.submitButtonDisabled = false;
        return;
      }
      if (this.paymentTransaction.invoice_number) {
        if (this.paymentTransaction.invoice_number.length > 15) {
          this.toasterService.clear();
          this.toasterService.error('Invoice number should be 15 characters');
          this.submitButtonDisabled = false;
          return false;
        }
      }
      if (this.paymentTransaction.invoice_number && (this.vends.length == 0)) {
        this.toasterService.clear();
        this.toasterService.warning('Vendors are not associated for this business');
        this.submitButtonDisabled = false;
        return false;
      } else if (this.paymentTransaction.invoice_number && (this.paymentTransaction.vendor_id === undefined || !this.paymentTransaction.vendor_id || isNaN(this.paymentTransaction.vendor_id))) {
        this.toasterService.clear();
        this.toasterService.error('Please select vendor');
        this.submitButtonDisabled = false;
        return;
      }
      /*multiple selection of tank or single object*/
      let pondIdsarr = [];
      if (this.pondselectedTankIds && this.pondselectedTankIds.length > 0) {
        for (let pondID of this.pondselectedTankIds) {
          const selectedPond = this.ponddropdownList.find(p => p.pondID === pondID);
          if (selectedPond) {
            let newobj = {
              pond_culture_id: selectedPond.id,
              pond_id: selectedPond.pondID,
              culture_identifier: selectedPond.cultureIdentifier,
              pond_identifier: selectedPond.pondIdentifier,
              isPreparatory: selectedPond.isPreparatory
            };
            pondIdsarr.push(newobj);
          }
        }
      }
      this.transaction.tank_ids = pondIdsarr;
      /*this.transaction.transaction_date = moment(this.transactionDateControl.value).format('YYYY-MM-DD HH:mm:ss')*/
      this.transaction.updated_by = localStorage.personID;
      this.transaction.created_name = this.LoginUserName;
      this.transaction.updated_name = this.LoginUserName;
      this.transaction.created_by = localStorage.personID;
      this.transaction.created_date = this.todayDate();
      this.transaction.updated_date = this.todayDate();
      if (((this.findinginventorytruefalse == true) && (this.transactionName == 'Balance migration(+)' || this.transactionName == 'Purchase(+)')) || ((this.findinginventorytruefalse == false) && (this.transactionName == 'Issue(-)' || this.transactionName == 'Reversal(-)'))) {
        this.transaction['unit_cost'] = (this.transaction.unit_cost); // if selected item type is expense then display on transaction type issue, reversal and display unit cost label
        // if selected item type is inventory then display all transaction type and if selected transaction type is  Purchase , balance migration and unit cost label will be display
      }
      if (this.transactionName == 'Reversal(-)') {
        this.transaction['quantity'] = (Number(this.transaction.quantity)) <= 0 ? (Number(this.transaction.quantity)) : (Number(this.transaction.quantity) * -1);
      }
      // if selected uom is gram then converted grams to kgs
      if (this.uomData[0].uomadded == true && this.transaction.uom_id == 3) {
        this.transaction['quantity'] = this.transaction.quantity / 1000;
        this.transaction['uom_id'] = 1;
      }
      this.paymentDetails = [];
      this.transaction.gst_percent = Number(this.transaction.gst_percent) || 0;
      this.transaction.gst_value = Number(this.transaction.quantity * this.transaction.unit_cost * this.transaction.gst_percent / 100);
      if (this.transaction.transaction_type_id == 1 || this.transaction.transaction_type_id == 2 || this.transaction.transaction_type_id == 4 || (this.transaction.transaction_type_id == 3 && this.findinginventorytruefalse == false)) {
        if ((this.paymentTransaction.paid_amount) && (this.vends.length == 0)) {
          this.toasterService.clear();
          this.toasterService.warning('Vendors are not associated for this business');
          this.submitButtonDisabled = false;
          return false;
        } else if ((this.paymentTransaction.paid_amount) && (this.paymentTransaction.vendor_id === undefined || !this.paymentTransaction.vendor_id || isNaN(this.paymentTransaction.vendor_id))) {
          this.toasterService.clear();
          this.toasterService.error('Please select vendor');
          this.submitButtonDisabled = false;
          return false;
        }
      }
      const totalCost = this.transaction.quantity && this.transaction.unit_cost && this.transaction.gst_percent ? this.transaction.gst_value + (this.transaction.quantity * this.transaction.unit_cost) : this.transaction.quantity * this.transaction.unit_cost;
      this.paymentTransaction.vendor_id = Number(this.paymentTransaction.vendor_id);
      if (this.paymentTransaction.paid_amount == totalCost) {
        this.paymentTransaction.payment_status = 'Fully';
      } else if (this.paymentTransaction.paid_amount < totalCost) {
        this.paymentTransaction.payment_status = 'Partially';
      } else if (this.paymentTransaction.paid_amount > totalCost) {
        this.toasterService.clear();
        this.toasterService.error('Amount should be lessthan value or same');
        this.submitButtonDisabled = false;
        return false;
      }
      this.transaction.quantity = Number(this.transaction.quantity);
      this.transaction.unit_cost = Number(this.transaction.unit_cost);
      if (this.loginPersonType != 'EMPLOYEE') {
        delete this.transaction.business_id;
        delete this.transaction.business_name;
        delete this.transaction.created_name;
        delete this.transaction.updated_name;
        delete this.transaction.created_by;
        delete this.transaction.updated_by;
      }
      let transactionInput = new Array();
      transactionInput.push(this.transaction);
      this.transactions = await this.transactionsService.createTransaction(transactionInput);
      if (this.transactions) {
        this.latestTransactionData = this.transactions;
        let image = this.imageFile;
        if (this.isFileSelected) {
          const upload = this.transactionsService.uploadFileToS3Bucket(this.imageFile, this.latestTransactionData.business_name, 'Material_Tx', `MM-${this.imageFile.name}`);
          upload.subscribe(async (res) => {
            if (res) {
              this.mtlAttachmentsModel.file_name = image.name;
              this.mtlAttachmentsModel.file_type = image.type.split('/')[1];
              this.mtlAttachmentsModel.attachment_path = Object.values(res)[0];
              this.mtlAttachmentsModel.transaction_id = Number(this.latestTransactionData.transactionResult[0].transactions[0].id);
              await this.mtlAttachmentsService.create(this.mtlAttachmentsModel);
            } else {
              this.toasterService.error('File upload failed');
              this.submitButtonDisabled = false;
            }
          });
        }
        this.toasterService.success('Transaction created successfully', '', {
          timeOut: 2000
        });
        const totalCost = this.transaction.quantity && this.transaction.unit_cost && this.transaction.gst_percent ? this.transaction.gst_value + (this.transaction.quantity * this.transaction.unit_cost) : this.transaction.quantity * this.transaction.unit_cost;
        if (((this.transaction.transaction_type_id == 1 || this.transaction.transaction_type_id == 2 || this.transaction.transaction_type_id == 4 || (this.transaction.transaction_type_id == 3 && this.findinginventorytruefalse == false)) && (this.paymentTransaction.vendor_id || this.paymentTransaction.paid_amount))) {
          this.submitButtonDisabled = true;
          await this.getPaymentTransaction(transactionInput, totalCost);
        }
        await this.ngOnInit();
        this.submitted = false;
        this.issubmitted = true;
        this.pondselectedItems = undefined;
        this.transaction.quantity = undefined;
        this.transaction.transaction_type_id = undefined;
        this.transactionTypes = [];
        this.transaction.uom_id = undefined;
        this.transaction.notes = undefined;
        this.transaction.gst_percent = undefined;
        this.transaction.gst_value = undefined;
        this.transaction.business_id = this.latestTransactionData.transactionResult[0].transactions[0].business_id;
        this.transaction.farm_site_id = this.latestTransactionData.transactionResult[0].transactions[0].farm_site_id;
        if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
          this.farmSites = await this.farmSiteService.getFarmeSitesByFarmsId(this.transaction.business_id);
          this.setSingleValueForFormLocation();
        } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
          this.farmSites = [];
          this.farmSites = await this.farmSiteService.getFarmeSitesBusinessDetailsByProviderId(this.transaction.business_id);
          this.setSingleValueForFormLocation();
        } else {
          this.farmSites = await this.farmSiteService.getFarmeSitesByFarmId(this.transaction.business_id);
          this.setSingleValueForFormLocation();
        }
        this.setSingleValueForFormLocation();
        await this.getAllItemMasterbyFarmer();
        this.transactionTypes = [];
        this.findinginventorytruefalse = true;
        this.transaction.pond_id = undefined;
        this.transactionName = undefined;
        this.item = undefined;
        this.transaction.transaction_date = moment(new Date()).format('DD-MMM-YYYY'); // "2020-05-20"
        this.submitButtonDisabled = false;
        this.submitted = false;    // Reset validation flag
        this.registerForm.markAsPristine(); // optional - clears dirty flags
        this.registerForm.markAsUntouched(); // optional - clears touched state
        this.pondselectedTankIds = [];
        this.registerForm.get('itemnameField').patchValue('');
        this.registerForm.get('vendorSelection').patchValue('');
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.submitButtonDisabled = false;
    }
  }

  /**
   * @method getPaymentTransaction
   * @async
   * @description Records a payment transaction associated with the material transaction.
   * @param {any} transaction - The transaction details.
   * @param {number} totalCost - The total cost of the transaction.
   */
  public async getPaymentTransaction(transaction, totalCost) {
    try {
      const itemIdsarr = [];
      const valuesArr = [];
      const quantity = [];
      const uomIds = [];
      if (transaction && transaction.length > 0) {
        for (let i = 0; i < transaction.length; i++) {
          itemIdsarr.push(transaction[i].item_id);
          valuesArr.push(Number(totalCost));
          quantity.push(transaction[i].quantity);
          uomIds.push(Number(transaction[i].uom_id));
        }
      }
      this.submitButtonDisabled = true;
      this.paymentTransaction.business_id = transaction[0].business_id;
      this.paymentTransaction.farm_site_id = transaction[0].farm_site_id;
      if (transaction[0].transaction_type_id == 1 || transaction[0].transaction_type_id == 2 || (transaction[0].transaction_type_id == 3 && this.findinginventorytruefalse == false)) {
        this.paymentTransaction.purchase_amount = Number(totalCost);
        if (this.paymentTransaction.paid_amount == null) {
          this.paymentTransaction.paid_amount = 0;
        }
        this.paymentTransaction.item_id = itemIdsarr;
        this.paymentTransaction.value = valuesArr;
        this.paymentTransaction.quantity = quantity;
        this.paymentTransaction.uom_id = uomIds;
        this.paymentTransaction.paid_amount = Number(this.paymentTransaction.paid_amount);
      }
      this.paymentTransaction.date = moment(this.transactionDateControl.value).format('YYYY-MM-DD')
      this.paymentTransaction.transaction_type_id = Number(transaction[0].transaction_type_id);
      this.paymentTransaction.vendor_id = Number(this.paymentTransaction.vendor_id);
      if (this.latestTransactionData.transactionResult[0].transactions[0].transaction_type_id == '4') {
        this.paymentTransaction.return_amount = this.latestTransactionData.transactionResult[0].transactions[0].total_cost;
      }
      this.paymentTransaction.material_transaction_id = [this.latestTransactionData.transactionResult[0].transactions[0].id];
      this.paymentTransaction = await this.paymentTransactionservice.addPaymentTransaction(this.paymentTransaction);
      this.paymentDetails = this.paymentTransaction;
      this.paymentTransaction.vendor_id = undefined;
      if ((this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Business Admin') && this.loginUserBusinessCategory != 'Farm') {
        this.vends = [];
      }
      this.paymentTransaction.paid_amount = undefined;
      this.paymentTransaction.return_amount = undefined;
      this.paymentTransaction = new PaymentTransactionModel();
      this.submitButtonDisabled = false;
      this.transaction.gst_percent = undefined;
      this.transaction.gst_value = undefined;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.submitButtonDisabled = false;
    }
  };

  /**
   * @method onhandquantity
   * @async
   * @description Fetches on-hand quantity and last purchased price for the selected item.
   */
  public async onhandquantity() {
    try {
      this.transactionOnHand = await this.transactionsService.onhandquantity(this.transaction.farm_site_id, this.transaction.item_id);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method todayDate
   * @description Returns the current date and time formatted as a string.
   * @returns {string} Formatted date string (YYYY-MM-DD HH:mm:ss).
   */
  public todayDate() {
    let year = new Date().getFullYear();
    let month = new Date().getMonth() + 1;
    let dateday = new Date().getDate();
    let hours = new Date().getHours();
    let minutes = new Date().getMinutes();
    let seconds = new Date().getSeconds();
    let dateStr = year + '-' + month + '-' + dateday + ' ' + hours + ':' + minutes + ':' + seconds;
    return dateStr;
  }

  /**
   * @method fileupload
   * @description Handles file selection for attachment upload.
   * Validates file size (max 2MB) and reads file as base64.
   */
  public fileupload(event: any) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 2097152) {
          this.toasterService.error('Image size should not exceed 2MB');
          $('#imageUpload').val('');
        } else {
          this.imageFile = file;
          this.isFileSelected = true;
          this.fileName = file.name;
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            const dataresult = '' + reader.result;
            this.selectedbase64file = dataresult;
          };
          reader.onerror = () => {

          };
        }
      } else {
        this.isFileSelected = false;
      }
    }
  }

  /**
   * @method browseFile
   * @description Triggers the file input click event.
   */
  public browseFile() {
    const fileInput = document.getElementById('imageUpload') as HTMLInputElement;
    fileInput.click(); // Trigger the file input click event
  }

  /**
   * @method remove
   * @description Removes the selected file attachment.
   * Clears file-related properties.
   */
  public remove() {
    const deleteimage = confirm('Are you sure you want to delete these image?');
    if (deleteimage) {
      this.imageFile = undefined;
      this.isFileSelected = false;
      this.isCleared = true;
      this.fileName = undefined;
      this.selectedbase64file = undefined;
      $('#imageUpload').val('');
    }
  }

  /**
   * @method clearTransactionData
   * @description Clears all transaction form data and resets the component state.
   * Called when the cancel button is clicked.
   */
  public clearTransactionData($event) {
    $('#imageUpload').val('');
    this.imageFile = undefined;
    this.isFileSelected = false;
    this.submitButtonDisabled = false;
    this.selectedbase64file = undefined;
    this.isCleared = false;
    this.fileName = undefined;
    this.transaction.transaction_type_id = undefined;
    this.transactionName = undefined;
    this.transaction.uom_id = undefined;
    this.transaction.business_item_id = undefined;
    this.vendorNameSearch = undefined;
    if ((this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Business Admin') && this.loginUserBusinessCategory != 'Farm') {
      this.farmSites = [];
      this.transaction.farm_site_id = undefined;
      this.transaction.item_id = '';
      this.item = undefined;
      this.transaction.business_id = '';
      this.businessItem = undefined;
      this.ponddropdownList = [];
      this.vends = [];
      this.registerForm.get('businessnameField').patchValue('');
    }
    this.transactionOnHand = {};
    this.transactionOnHand.onHandQuantities = [];
    this.transactionOnHand.transaction = [];
    this.transaction.quantity = undefined;
    this.transaction.unit_cost = undefined;
    this.transaction.notes = undefined;
    this.pondselectedItems = undefined;
    this.paymentTransaction.vendor_id = undefined;
    this.paymentTransaction.paid_amount = undefined;
    this.item = undefined;
    this.transaction.item_id = '';
    this.paymentTransaction.return_amount = undefined;
    $event.stopPropagation();
    $event.preventDefault();
    this.transaction.transaction_date = (moment(new Date()).format('DD-MMM-YYYY')).toString(); // "2020-05-20"
    this.submitted = false;
    this.registerForm.markAsPristine(); // optional - clears dirty flags
    this.registerForm.markAsUntouched(); // optional - clears touched state
    this.registerForm.get('itemnameField').patchValue('');
    this.pondselectedTankIds = []
  }

  /**
   * @method findTransactiontypeName
   * @description Finds and sets the transaction type name based on the selected ID.
   * Also triggers UOM data preparation.
   */
  public findTransactiontypeName() {
    let transactionTypeId = this.transaction.transaction_type_id;
    let itmsArr = _.filter(this.transactionTypes, function (o) {
      return (o.id as any) == transactionTypeId;
    });
    if (itmsArr.length > 0) {
      this.transactionName = itmsArr[0].transaction_type;
      this.prepareUOMData(this.businessitmsArr[0]);
    }
  }

  /**
   * @method numberValidation
   * @description Validates input to allow only numbers and a single decimal point.
   * Prevents invalid characters.
   */
  public numberValidation(event: any) {
    const key = event.key;
    const value = event.target.value;
    // Allow numbers, the dot (decimal point), and the Backspace key
    if (
      !/^\d$/.test(key) && // Check if the entered character is a number
      key !== '.' && // Check if the entered character is a dot (decimal point)
      key !== 'Backspace' // Check if the Backspace key was pressed
    ) {
      event.preventDefault();
    }

    // Allow only one dot (decimal point)
    if (key === '.' && value.includes('.')) {
      event.preventDefault();
    }

    // Disallow the first letter being a dot
    if (key === '.' && value === '') {
      event.preventDefault();
    }
  }

  /**
   * @method calculateTransactionValue
   * @description Calculates the total transaction value based on quantity, unit cost, and GST.
   * @returns {string} The calculated value formatted to 2 decimal places.
   */
  calculateTransactionValue(): string {
    const qty = this.transaction.quantity || 0;
    const cost = this.transaction.unit_cost || 0;
    const gst = this.transaction.gst_percent || 0;

    let value = qty * cost;

    if (gst > 0) {
      value = value * (1 + gst / 100);
    }

    return value.toFixed(2);
  }


  /**
   * @method initializedatePicker
   * @description Initializes the jQuery datepicker for the transaction date field.
   */
  public initializedatePicker() {
    let strArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let contract_dates = $('#startdate').datepicker({
      maxDate: new Date(),
      onSelect: function (selectedDate) {
        let parsedDate = (selectedDate.split('/')[1] + '-' + strArray[(parseInt(selectedDate.split('/')[0]) - 1)] + '-' + selectedDate.split('/')[2]);
        let option = this.id == 'startdate' ? 'minDate' : 'maxDate',
          instance = $(this).data('datepicker'),
          date = $.datepicker.parseDate(instance.settings.dateFormat || $.datepicker._defaults.dateFormat, selectedDate, instance.settings);
        contract_dates.not(this).datepicker('option', option, date);
        $('#startdate').val(parsedDate);
      }
    });
  }

  /**
   * @method filterBusinesses
   * @description Filters business entities based on search input.
   * Updates `filteredOptions`.
   */
  public filterBusinesses(): void {
    const value = (this.registerForm.get('businessnameField')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businessEntities.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method selectOption
   * @async
   * @description Handles business selection from dropdown.
   * Resets related fields and triggers data fetch for the selected business.
   */
  public async selectOption(business: BusinessEntity) {
    this.registerForm.get('businessnameField')!.setValue(business.businessName);
    // this.isDropdownOpen = false;
    this.businessSearch = business.businessName;
    this.transaction.business_id = business.id;
    if (this.transaction.business_id) {
      this.transaction.farm_site_id = undefined;
      this.getitemdatafarmsitedta();
      this.getBussinessNamebyBussinessId();
      this.getVendors();
      this.vends = [];
      this.itemMasters = [];
      this.item = undefined;
      this.registerForm.get('itemnameField').patchValue('');
      this.ponddropdownList = [];
      this.pondselectedItems = [];
    }
  }

  /** @type {any} Selected tank IDs for pond multi-select. */
  pondselectedTankIds: any;
}
